/** @format */

import React, { Suspense } from "react";
import { Markup, TransformCallback, Node } from "interweave";
import { ReactComponent as Youtube } from "bootstrap-icons/icons/youtube.svg";
import { ReactComponent as Link } from "bootstrap-icons/icons/link.svg";
import { useAppSelector } from "../../app/hooks";
import { selectIsPrintable } from "../printable/printableSlice";
import GlossaryTerms from "../glossaryTerms/GlossaryTerms";
import Maths from "./Maths";

const Latex = React.lazy(() => import("../latex/Latex"));

export type FormattedTextProps = {
  html: string;
};

const FormattedText: React.FC<FormattedTextProps> = ({ html }) => {
  const processedHTML = React.useMemo(() => {
    const stringifiedHTML = typeof html === "string" ? html : String(html);
    return stringifiedHTML
      .replace(/\\\([\s\S]+?\\\)/g, "<latex>$&</latex>")
      .replace(/\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]/g, "<latex block>$&</latex>");
  }, [html]);

  const isPrintable = useAppSelector(selectIsPrintable());

  const transformer: TransformCallback = (
    node: HTMLElement,
    children: Node[],
  ): React.ReactNode | undefined => {
    let output;

    // Mathjax / KaTeX Block
    if (
      node.tagName === "LATEXBLOCK" ||
      (node.tagName === "LATEX" &&
        node.hasAttribute("block") &&
        node.getAttribute("block") !== "false")
    ) {
      output = (
        <Suspense fallback={<div>{children}</div>}>
          <Latex block={true}>{children}</Latex>
        </Suspense>
      );
    }
    // Mathjax Inline
    else if (node.tagName === "LATEX") {
      output = (
        <Suspense fallback={<span>{children}</span>}>
          <Latex>{children}</Latex>
        </Suspense>
      );
    } else if (node.tagName === "math") {
      output = <Maths node={node} />;
    }
    // Glossary Terms
    else if (node.tagName === "SPAN" && node.getAttribute("data-glossary")) {
      const tooltip = (node.getAttribute("data-glossary") || "").replace(
        /<[/]?latex[^>]*>/g,
        "",
      );

      output = <GlossaryTerms tooltip={tooltip}>{children}</GlossaryTerms>;
    }
    // iFrame & YouTube
    else if (node.tagName === "IFRAME") {
      const iframeSrc = node.getAttribute("src");
      if (isPrintable) {
        const isYoutubeVideo = iframeSrc?.match(
          /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
        );
        if (isYoutubeVideo) {
          output = (
            <>
              <Youtube />
              &nbsp;&nbsp;https://youtu.be/{isYoutubeVideo[1]}
            </>
          );
        } else {
          output = (
            <>
              <Link />
              &nbsp;&nbsp;{iframeSrc}
            </>
          );
        }
      } else {
        output = (
          <iframe
            title={iframeSrc ? iframeSrc : undefined}
            src={iframeSrc?.replace("youtube", "youtube-nocookie")}
            className={node.getAttribute("class") || undefined}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
            allowFullScreen
          />
        );
      }
    }

    return output;
  };

  return (
    <Markup
      key={processedHTML}
      noWrap={true}
      transform={transformer}
      content={processedHTML}
    />
  );
};

export default FormattedText;
